import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SetNewPasswordStep from './SetNewPasswordStep';

// Mandatory prompt after an admin-initiated password reset — Login.jsx
// routes here instead of /dashboard whenever the login response reports
// mustChangePassword (see adminUsers.js's reset-password endpoint, which
// sets attributes.must_change_password; authSession.js/login.js surface it
// back). Unlike ForgotPassword.jsx/ResetPassword.jsx, identity here is
// already proven — the person is signed in with our own JWT — so this
// hits the authenticated /api/account/change-password endpoint instead of
// the token-based /password-reset/complete.

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

const page = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

const card = {
  background: 'rgba(255, 255, 255, 0.65)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  borderRadius: '16px',
  padding: '48px 40px',
  width: '100%',
  maxWidth: '420px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
};

export default function ChangePasswordRequired() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  // Phase 2 of the httpOnly-cookie migration (see AuthContext.js's own
  // header comment) — no more synchronous "is there a token" pre-check
  // possible. Deliberately does NOT gate on useAuth()'s `user` either —
  // Login.jsx's mustChangePassword branch intentionally does NOT call
  // refreshUser() before navigating here (see its own comment on why:
  // doing so re-renders this page and races its own navigate('/dashboard')
  // effect), so `user` may genuinely still be null/loading at this exact
  // moment even though a real session was just established. Relying on it
  // here would wrongly bounce a legitimate arrival. handleSubmit's own
  // 401 handling below covers the actual "no session" case instead.
  async function handleSubmit(password) {
    const res = await fetch(`${API_BASE}/account/change-password`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newPassword: password }),
    });
    if (res.status === 401) {
      navigate('/');
      return { error: 'Session expired, please log in again.' };
    }
    const data = await res.json();
    return res.ok ? { ok: true } : { error: data.error };
  }

  async function handleSuccess() {
    await refreshUser();
    // Same precedence Login.jsx itself uses after a normal sign-in.
    const needsProfile = localStorage.getItem('bescom_needs_profile') === '1';
    navigate(needsProfile ? '/complete-profile' : '/dashboard');
  }

  return (
    <div style={page}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/bescom-logo-ui.png" alt="BESCOM" style={{ width: '80px', marginBottom: '16px' }} />
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px' }}>
            Set a new password
          </h1>
          <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
            Your password was reset by an administrator. Choose your own password to continue.
          </p>
        </div>

        <SetNewPasswordStep
          onSubmit={handleSubmit}
          onSuccess={handleSuccess}
          description="This replaces the temporary password you were given."
          submitLabel="Set Password"
        />
      </div>
    </div>
  );
}
